import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, Polygon as LPolygon, Tooltip, useMap } from 'react-leaflet';
import { Grab, Tier, Vertrag, Kunde, Polygon, GRAB_STATUS, ZAHLUNGSINTERVALL, CreateVertragsartikelRequest } from '../types';
import grabService from '../services/grabService';
import tierService from '../services/tierService';
import vertragService from '../services/vertragService';
import kundeService from '../services/kundeService';
import { Card, Badge, Spinner, Icon, Button, Field, Input, DateInput, Select, Textarea, FormActions, LoadingScreen } from '../components/ui';
import { STATUS_FILL, STATUS_LABEL, ringToLatLngs, LAGEPLAN_BEARING } from '../components/lageplan/leafletUtils';
import KundeFormModal from '../components/KundeFormModal';
import KundeSelect from '../components/KundeSelect';
import KommentarSektion from '../components/KommentarSektion';
import DokumentSektion from '../components/DokumentSektion';
import TierFormular from '../components/TierFormular';
import PositionenErfassung, { PositionEntwurf, positionenZuRequests } from '../components/PositionenErfassung';
import RueckwirkendDialog, { brauchtRueckwirkendFrage } from '../components/RueckwirkendDialog';
import VerlaengerungErfassung, { standardVerlaengerung, verlaengerungZuRequest } from '../components/VerlaengerungErfassung';
import { vertragDefaultsFuerTier } from '../utils/vertragDefaults';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { ZurueckLink } from '../components/ZurueckLink';

// leaflet-rotate erwartet ein globales `L`. Nach den Imports setzen und das Plugin via require
// laden (gleiches Muster wie in LageplanKarte), damit die Mini-Karte gedreht werden kann.
(window as any).L = L;
// eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
require('leaflet-rotate');

// Mini-Karte fest gedreht (kein Bedien-Element, keine Nutzer-Drehung), damit das Raster gerade liegt.
const ROTATE_OPTIONS = {
  rotate: true,
  bearing: LAGEPLAN_BEARING,
  rotateControl: false,
  touchRotate: false,
  shiftKeyRotate: false,
} as any;

const statusBadge: Record<string, string> = {
  FREI: 'bg-green-100 text-green-800', BELEGT: 'bg-gray-200 text-gray-800',
  RESERVIERT: 'bg-yellow-100 text-yellow-800', IN_NUTZUNG: 'bg-blue-100 text-blue-800',
};

const intervallLabels: Record<string, string> = {
  MONATLICH: 'Monatlich', QUARTALSWEISE: 'Quartalsweise', HALBJAEHRLICH: 'Halbjährlich', JAEHRLICH: 'Jährlich',
};

// ISO-Datum (yyyy-mm-dd) -> dd.mm.yyyy, ohne Zeitzonen-Verschiebung.
const fmtDatum = (iso?: string): string => {
  if (!iso) return '–';
  const [y, m, d] = iso.split('T')[0].split('-');
  return (y && m && d) ? `${d}.${m}.${y}` : iso;
};

const heute = () => new Date().toISOString().split('T')[0];

const SELECTED_COLOR = '#059669'; // brand-600

const centroid = (ring: Polygon): [number, number] => {
  let lat = 0, lon = 0;
  ring.forEach(([x, y]) => { lon += x; lat += y; });
  return [lat / ring.length, lon / ring.length];
};

/** Zoomt maximal auf das gewählte Grab und zeigt dabei die unmittelbar umliegenden Gräber. */
const FitToGrave: React.FC<{ ring: Polygon }> = ({ ring }) => {
  const map = useMap();
  useEffect(() => {
    const m = map as any;
    const bounds = L.latLngBounds(ringToLatLngs(ring)).pad(1.2);
    const apply = () => { m.setBearing?.(LAGEPLAN_BEARING); map.fitBounds(bounds, { maxZoom: 25, animate: false }); };
    apply();
    const t = window.setTimeout(() => { map.invalidateSize(); apply(); }, 120);
    return () => window.clearTimeout(t);
  }, [map, ring]);
  return null;
};

/** Kleiner Kartenausschnitt auf weißem Grund: gewähltes Grab hervorgehoben, Nachbarn blass. */
const GrabMiniMap: React.FC<{ grab: Grab; alle: Grab[] }> = ({ grab, alle }) => {
  const ring = grab.lageplanPolygon;
  const center = useMemo(() => (ring && ring.length >= 3 ? centroid(ring) : null), [ring]);

  const neighbors = useMemo(() => {
    if (!center) return [];
    const [clat, clon] = center;
    return alle.filter(g => {
      if (g.id === grab.id || !g.lageplanPolygon || g.lageplanPolygon.length < 3) return false;
      const [glat, glon] = centroid(g.lageplanPolygon);
      return Math.abs(glat - clat) < 0.0005 && Math.abs(glon - clon) < 0.0008;
    });
  }, [alle, grab.id, center]);

  if (!center || !ring) {
    return (
      <div className="grid h-full min-h-[260px] place-items-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
        Für dieses Grab ist keine Geometrie im Lageplan hinterlegt.
      </div>
    );
  }

  return (
    <div className="h-full min-h-[320px] overflow-hidden rounded-xl ring-1 ring-black/10">
      <MapContainer
        center={center}
        zoom={22}
        maxZoom={25}
        scrollWheelZoom
        style={{ height: '100%', width: '100%', minHeight: 320, background: '#fff' }}
        {...ROTATE_OPTIONS}
      >
        <FitToGrave ring={ring} />
        {neighbors.map(g => (
          <LPolygon
            key={g.id}
            positions={ringToLatLngs(g.lageplanPolygon!)}
            pathOptions={{ color: STATUS_FILL[g.status] || '#6b7280', weight: 1, opacity: 0.8, fillOpacity: 0.25 }}
          />
        ))}
        <LPolygon
          positions={ringToLatLngs(ring)}
          pathOptions={{ color: SELECTED_COLOR, weight: 3, fillColor: SELECTED_COLOR, fillOpacity: 0.5 }}
        >
          <Tooltip permanent direction="top">{grab.abteilung}/{grab.grabnummer}</Tooltip>
        </LPolygon>
      </MapContainer>
    </div>
  );
};

/** Beschriftete Detailzeile; rendert nichts, wenn kein Wert vorhanden ist. */
const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 py-2.5 text-sm">
      <span className="shrink-0 text-gray-500">{label}</span>
      <span className="min-w-0 break-words text-right font-medium text-gray-900">{value}</span>
    </div>
  );
};

const SectionTitle: React.FC<{ icon: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }> = ({ icon, action, children }) => (
  <div className="mb-3 flex items-center justify-between gap-2">
    <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
      <span className="text-gray-400">{icon}</span>{children}
    </h3>
    {action}
  </div>
);

const EditLink: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="rounded-md px-2 py-0.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50">
    {children}
  </button>
);

const d = (iso?: string) => (iso ? iso.split('T')[0] : ''); // ISO -> yyyy-mm-dd für date-Inputs

// Was gerade inline bearbeitet wird; per Tier-/Vertrags-ID adressiert.
type EditTarget =
  | { kind: 'grab' }
  | { kind: 'kunde' }
  | { kind: 'tier'; tierId?: number }          // tierId undefined = neues Tier
  | { kind: 'vertragNeu'; tierId: number }
  | { kind: 'vertrag'; vertragId: number }
  | { kind: 'vertragBeenden'; vertragId: number }
  | null;

/**
 * Grab-Detailseite (/graeber/:id): zeigt Stammdaten, die aktuell beigesetzten Tiere (je mit
 * eigenem, optionalem Vertrag/Kunde), eine Belegungs-Historie und einen Kartenausschnitt.
 * Mehrere Tiere pro Grab sind möglich; ein Tier wandert in die Historie, sobald sein Vertrag
 * beendet ist. Alles direkt auf der Seite bearbeitbar.
 */
const GrabDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Kam der Nutzer über das Lageplan-Popup hierher? Dann führt "Zurück" auf die Karte (an dieselbe Stelle).
  const fromLageplan = (location.state as any)?.from === 'lageplan';
  const grabId = id ? parseInt(id) : NaN;

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Vertrag weit in der Vergangenheit: wartet auf die Antwort „ab jetzt oder rückwirkend?“.
  const [rueckFrage, setRueckFrage] = useState<CreateVertragsartikelRequest[] | null>(null);
  const [grabState, setGrabState] = useState<Grab | null>(null);
  const [alleGraeber, setAlleGraeber] = useState<Grab[]>([]);
  const [tiere, setTiere] = useState<Tier[]>([]);
  const [vertraege, setVertraege] = useState<Vertrag[]>([]);
  const [directKunde, setDirectKunde] = useState<Kunde | null>(null);
  const [kunden, setKunden] = useState<Kunde[]>([]);

  const [editing, setEditing] = useState<EditTarget>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({});
  const [showNeuerKunde, setShowNeuerKunde] = useState(false);
  // Letzter bekannter Grab-Status: ändert er sich durch eine Aktion, tauscht der Badge sichtbar.
  // Mit Grab-ID, sonst gälte beim Wechsel zu einem anderen Grab (Minikarte) dessen Status als Änderung.
  const letzterStatus = useRef<{ id: number; status: string } | null>(null);

  const refresh = useCallback(async () => {
    if (!grabId || Number.isNaN(grabId)) { setNotFound(true); setLoading(false); return; }
    setLoading(true);
    try {
      const g = await grabService.getById(grabId);
      const [ts, vs, neighbors] = await Promise.all([
        tierService.getAllByGrab(grabId),
        vertragService.getByGrab(grabId),
        grabService.getByFriedhof(g.friedhofId, { includePolygon: true }), // Minikarte braucht Nachbar-Polygone
      ]);
      const k = g.kundeId ? await kundeService.getById(g.kundeId).catch(() => null) : null;
      const vorher = letzterStatus.current;
      if (vorher && vorher.id === g.id && vorher.status !== g.status) markiereNeu(`grabstatus-${g.id}`);
      letzterStatus.current = { id: g.id, status: g.status };
      setGrabState(g); setAlleGraeber(neighbors); setTiere(ts); setVertraege(vs); setDirectKunde(k);
    } catch {
      if (!grabState) setNotFound(true);
      else { setTiere([]); setVertraege([]); setDirectKunde(null); }
    } finally {
      setLoading(false);
    }
  }, [grabId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  // Kundenliste für die Zuordnung in Grab-/Vertragsformularen (friedhofsunabhängig, einmal laden).
  useEffect(() => {
    kundeService.getAll().then(setKunden).catch(() => {/* Zuordnung optional */});
  }, []);

  // Vertrag je Tier (jeweils der aktuellste).
  const vertragByTier = useMemo(() => {
    const m = new Map<number, Vertrag>();
    vertraege.forEach(v => { if (v.tierId != null && !m.has(v.tierId)) m.set(v.tierId, v); });
    return m;
  }, [vertraege]);

  // Aktuelle Belegung = Tiere ohne Vertrag oder mit aktivem Vertrag; Historie = beendeter Vertrag.
  const { aktuelleTiere, historieTiere } = useMemo(() => {
    const aktuell: Tier[] = []; const hist: Tier[] = [];
    tiere.forEach(t => {
      const v = vertragByTier.get(t.id);
      if (v && v.status === 'BEENDET') hist.push(t); else aktuell.push(t);
    });
    return { aktuelleTiere: aktuell, historieTiere: hist };
  }, [tiere, vertragByTier]);

  const kundeAdresse = directKunde
    ? [[directKunde.strasse, directKunde.hausnummer].filter(Boolean).join(' '), [directKunde.plz, directKunde.ort].filter(Boolean).join(' ')]
        .filter(Boolean).join(', ')
    : '';

  const startGrab = () => {
    if (!grabState) return;
    setForm({ abteilung: grabState.abteilung, grabnummer: grabState.grabnummer, status: grabState.status, notizen: grabState.notizen || '', active: grabState.active });
    setEditing({ kind: 'grab' });
  };
  const startKunde = () => {
    setForm({ kundeId: grabState?.kundeId || 0 });
    setEditing({ kind: 'kunde' });
  };
  const startTier = (tier?: Tier) => setEditing({ kind: 'tier', tierId: tier?.id });
  const startVertrag = (v: Vertrag) => {
    setForm({ kundeId: v.kundeId, unterschriftsdatum: d(v.unterschriftsdatum), laufzeitJahre: v.laufzeitJahre, zahlungsintervall: v.zahlungsintervall, autoVerlaengerung: v.autoVerlaengerung, verlaengerungJahre: v.verlaengerungJahre, notizen: v.notizen || '' });
    setEditing({ kind: 'vertrag', vertragId: v.id });
  };
  // Neuer Vertrag für ein Tier, vorbefüllt: Kunde = Grab-Kunde, Unterschrift = Beisetzung,
  // Laufzeit nach Tiergewicht (Regel in utils/vertragDefaults, geteilt mit der Kunden-Detailseite).
  const startVertragNeu = (tier: Tier) => {
    const { unterschriftsdatum, laufzeitJahre } = vertragDefaultsFuerTier(tier);
    setForm({ kundeId: grabState?.kundeId || tier.kundeId || 0, unterschriftsdatum, laufzeitJahre, notizen: '', positionen: [] as PositionEntwurf[], verlaengerung: standardVerlaengerung });
    setEditing({ kind: 'vertragNeu', tierId: tier.id });
  };
  const startVertragBeenden = (v: Vertrag) => {
    setForm({ endeDatum: heute() });
    setEditing({ kind: 'vertragBeenden', vertragId: v.id });
  };
  const cancel = () => { setEditing(null); setForm({}); };

  const afterSave = async (msg: string) => {
    toast.success(msg);
    cancel();
    await refresh();
  };

  const saveGrab = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grabState) return;
    setSaving(true);
    try {
      await grabService.update(grabState.id, {
        friedhofId: grabState.friedhofId, abteilung: form.abteilung, grabnummer: form.grabnummer,
        status: form.status, notizen: form.notizen || undefined,
        kundeId: grabState.kundeId ?? undefined, active: form.active,
      });
      markiereNeu(`grab-${grabState.id}`);
      await afterSave('Grab gespeichert');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Grab konnte nicht gespeichert werden'); }
    finally { setSaving(false); }
  };

  // Direkte Kundenzuordnung des Grabs (in der Kunde-Sektion verwaltet, unabhängig vom Vertrag).
  const saveKunde = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grabState) return;
    setSaving(true);
    try {
      await grabService.update(grabState.id, {
        friedhofId: grabState.friedhofId, abteilung: grabState.abteilung, grabnummer: grabState.grabnummer,
        status: grabState.status, notizen: grabState.notizen || undefined,
        kundeId: form.kundeId || undefined, active: grabState.active,
      });
      markiereNeu(`grabkunde-${grabState.id}`);
      await afterSave(form.kundeId ? 'Kunde zugeordnet' : 'Kundenzuordnung entfernt');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Kundenzuordnung konnte nicht gespeichert werden'); }
    finally { setSaving(false); }
  };

  const deleteTier = async (tier: Tier) => {
    if (!window.confirm(`Tier „${tier.name || 'ohne Name'}“ löschen?`)) return;
    try {
      await tierService.delete(tier.id);
      toast.success('Tier gelöscht');
      await refresh();
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Tier konnte nicht gelöscht werden'); }
  };

  const saveVertrag = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grabState || editing?.kind !== 'vertrag') return;
    if (!form.kundeId) { toast.error('Bitte einen Kunden wählen'); return; }
    const existing = vertraege.find(v => v.id === editing.vertragId);
    setSaving(true);
    try {
      await vertragService.update(editing.vertragId, {
        kundeId: parseInt(form.kundeId), grabId: grabState.id, tierId: existing?.tierId,
        unterschriftsdatum: form.unterschriftsdatum, laufzeitJahre: parseInt(form.laufzeitJahre),
        autoVerlaengerung: form.autoVerlaengerung, verlaengerungJahre: parseInt(form.verlaengerungJahre),
        zahlungsintervall: form.zahlungsintervall, notizen: form.notizen || undefined, active: true,
      });
      markiereNeu(`vertrag-${editing.vertragId}`);
      await afterSave('Vertrag gespeichert');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Vertrag konnte nicht gespeichert werden'); }
    finally { setSaving(false); }
  };

  const saveVertragNeu = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grabState || editing?.kind !== 'vertragNeu') return;
    if (!form.kundeId) { toast.error('Bitte einen Kunden wählen'); return; }
    const positionen = positionenZuRequests(form.positionen ?? []);
    if (typeof positionen === 'string') { toast.error(positionen); return; }
    const verlaengerung = verlaengerungZuRequest(form.verlaengerung ?? standardVerlaengerung);
    if (typeof verlaengerung === 'string') { toast.error(verlaengerung); return; }
    if (verlaengerung) positionen.push(verlaengerung);
    if (brauchtRueckwirkendFrage(form.unterschriftsdatum, positionen.length > 0)) {
      setRueckFrage(positionen);
      return;
    }
    await anlegeVertrag(positionen);
  };

  const anlegeVertrag = async (positionen: CreateVertragsartikelRequest[], abrechnungAb?: string) => {
    if (!grabState || editing?.kind !== 'vertragNeu') return;
    setSaving(true);
    try {
      const vertrag = await vertragService.create({
        kundeId: parseInt(form.kundeId), grabId: grabState.id, tierId: editing.tierId,
        unterschriftsdatum: form.unterschriftsdatum, laufzeitJahre: parseInt(form.laufzeitJahre) || 1,
        notizen: form.notizen || undefined,
        autoVerlaengerung: !!form.verlaengerung?.aktiv,
        positionen,
        erstrechnung: positionen.length > 0,
        abrechnungAb,
      });
      setRueckFrage(null);
      markiereNeu(`vertrag-${vertrag.id}`);
      // Nächster Schritt sind die Rechnungsentwürfe: prüfen und stellen.
      if (vertrag.erstrechnungId) {
        const anzahl = vertrag.erstrechnungIds?.length ?? 1;
        toast.success(anzahl > 1
          ? `Vertrag angelegt, dazu ${anzahl} Rechnungsentwürfe (je Vertragsjahr). Bitte prüfen und stellen.`
          : 'Vertrag angelegt. Bitte den Rechnungsentwurf prüfen und stellen.', { duration: 6000 });
        navigate(anzahl > 1 ? '/rechnungen' : `/rechnungen/${vertrag.erstrechnungId}`);
        return;
      }
      if (vertrag.erstrechnungHinweis) toast(vertrag.erstrechnungHinweis, { icon: 'ℹ️', duration: 8000 });
      await afterSave('Vertrag angelegt');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Vertrag konnte nicht angelegt werden'); }
    finally { setSaving(false); }
  };

  const saveVertragBeenden = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing?.kind !== 'vertragBeenden') return;
    setSaving(true);
    try {
      await vertragService.beenden(editing.vertragId, { endeDatum: form.endeDatum });
      // Das Tier wandert in die Historie und leuchtet dort auf.
      const tierId = vertraege.find(v => v.id === editing.vertragId)?.tierId;
      if (tierId != null) markiereNeu(`tier-${tierId}`);
      markiereNeu(`vertrag-${editing.vertragId}`);
      await afterSave('Vertrag beendet');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Vertrag konnte nicht beendet werden'); }
    finally { setSaving(false); }
  };

  if (loading && !grabState) return <LoadingScreen />;
  if (notFound || !grabState) return <div className="p-6">Grab nicht gefunden. <Link to="/graeber" className="text-brand-600">Zurück</Link></div>;

  // Gemeinsames Vertrags-Formular (anlegen + bearbeiten).
  const vertragForm = (submit: (e: React.FormEvent) => void, isNew: boolean) => (
    <form onSubmit={submit} className="ui-aufklappen mt-3 space-y-3 rounded-lg bg-gray-50 p-3">
      <Field label="Kunde" required>
        <KundeSelect value={form.kundeId ? parseInt(form.kundeId) : undefined} kunden={kunden} onChange={id => setForm({ ...form, kundeId: id ? String(id) : '' })} allowClear={false} placeholder="Kunde suchen…" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Unterschrift" required><DateInput required value={form.unterschriftsdatum} onChange={v => setForm({ ...form, unterschriftsdatum: v })} /></Field>
        <Field label="Laufzeit (Jahre)" required><Input type="number" min="1" required value={form.laufzeitJahre} onChange={e => setForm({ ...form, laufzeitJahre: e.target.value })} /></Field>
      </div>
      {/* Zahlungsintervall/Verlängerung steuern die Abrechnung nicht mehr – beim Anlegen zählen nur
          die beauftragten Leistungen. Beim Bearbeiten bleiben sie für Altverträge sichtbar. */}
      {!isNew && (
        <>
          <Field label="Zahlungsintervall"><Select value={form.zahlungsintervall} onChange={e => setForm({ ...form, zahlungsintervall: e.target.value })}>{ZAHLUNGSINTERVALL.map(z => <option key={z} value={z}>{intervallLabels[z] || z}</option>)}</Select></Field>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.autoVerlaengerung} onChange={e => setForm({ ...form, autoVerlaengerung: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
            Automatische Verlängerung
          </label>
          {form.autoVerlaengerung && (
            <Field label="Verlängerung (Jahre)"><Input type="number" min="1" value={form.verlaengerungJahre} onChange={e => setForm({ ...form, verlaengerungJahre: e.target.value })} /></Field>
          )}
        </>
      )}
      {isNew && (
        <PositionenErfassung
          positionen={form.positionen ?? []}
          onChange={positionen => setForm((f: any) => ({ ...f, positionen }))}
          unterschriftsdatum={form.unterschriftsdatum || undefined}
        >
          <VerlaengerungErfassung
            wert={form.verlaengerung ?? standardVerlaengerung}
            onChange={verlaengerung => setForm((f: any) => ({ ...f, verlaengerung }))}
            unterschriftsdatum={form.unterschriftsdatum || undefined}
            laufzeitJahre={parseInt(form.laufzeitJahre) || undefined}
          />
        </PositionenErfassung>
      )}
      <Field label="Notizen"><Textarea rows={2} value={form.notizen} onChange={e => setForm({ ...form, notizen: e.target.value })} /></Field>
      {isNew && <p className="text-xs text-gray-500">Vorausgefüllt aus Tier/Grab – frei änderbar. Beim Anlegen entsteht der Entwurf der ersten Rechnung – Sie prüfen und stellen ihn danach. Ein freies Grab wird auf „In Nutzung“ gesetzt.</p>}
      <FormActions>
        <Button type="button" variant="secondary" onClick={cancel}>Abbrechen</Button>
        <Button type="submit" loading={saving}>{isNew ? 'Vertrag anlegen' : 'Speichern'}</Button>
      </FormActions>
    </form>
  );

  // Vertrags-Block innerhalb einer Tier-Karte.
  const vertragBlock = (tier: Tier) => {
    const v = vertragByTier.get(tier.id);
    if (editing?.kind === 'vertragNeu' && editing.tierId === tier.id) return vertragForm(saveVertragNeu, true);
    if (editing?.kind === 'vertrag' && v && editing.vertragId === v.id) return vertragForm(saveVertrag, false);
    if (editing?.kind === 'vertragBeenden' && v && editing.vertragId === v.id) {
      return (
        <form onSubmit={saveVertragBeenden} className="ui-aufklappen mt-3 space-y-3 rounded-lg bg-amber-50 p-3">
          <p className="text-xs text-amber-900">Vertrag {v.vertragsnummer} beenden. Sind danach keine aktiven Verträge mehr am Grab, wird es wieder „Frei“. Die Tierdaten bleiben als Historie erhalten.</p>
          <Field label="Enddatum" required><DateInput required value={form.endeDatum} onChange={val => setForm({ ...form, endeDatum: val })} /></Field>
          <FormActions>
            <Button type="button" variant="secondary" onClick={cancel}>Abbrechen</Button>
            <Button type="submit" loading={saving}>Vertrag beenden</Button>
          </FormActions>
        </form>
      );
    }
    if (!v) {
      return (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2">
          <span className="text-sm text-gray-500">Kein Vertrag</span>
          <Button size="sm" variant="secondary" onClick={() => startVertragNeu(tier)} icon={<Icon.Plus className="h-4 w-4" />}>Vertrag anlegen</Button>
        </div>
      );
    }
    const a = ankunft(`vertrag-${v.id}`);
    return (
      <div ref={a.ref} className={`mt-3 rounded-lg bg-gray-50 p-3 ${a.className}`}>
        <div className="flex items-center justify-between gap-2">
          <Link to={`/vertraege/${v.id}`} className="text-sm font-medium text-brand-700 hover:underline">Vertrag {v.vertragsnummer}</Link>
          <div className="flex items-center gap-1">
            <EditLink onClick={() => startVertrag(v)}>Bearbeiten</EditLink>
            <button type="button" onClick={() => startVertragBeenden(v)} className="rounded-md px-2 py-0.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-50">Beenden</button>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          <Row label="Kunde" value={v.kundeId ? <Link to={`/kunden/${v.kundeId}`} className="text-brand-700 hover:underline">{v.kundeName}</Link> : v.kundeName} />
          <Row label="Unterschrift" value={fmtDatum(v.unterschriftsdatum)} />
          <Row label="Laufzeit" value={`${v.laufzeitJahre} Jahr(e)`} />
          <Row label="Zahlungsintervall" value={intervallLabels[v.zahlungsintervall] || v.zahlungsintervall} />
          <Row label="Auto-Verlängerung" value={v.autoVerlaengerung ? `Ja (+${v.verlaengerungJahre} J.)` : 'Nein'} />
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      {rueckFrage && (
        <RueckwirkendDialog
          unterschriftsdatum={form.unterschriftsdatum}
          saving={saving}
          onWahl={ab => anlegeVertrag(rueckFrage, ab)}
          onAbbrechen={() => setRueckFrage(null)}
        />
      )}
      {fromLageplan ? (
        <Link to={`/lageplan/${grabState.friedhofId}`} state={{ restore: true }} className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-900">
          <Icon.ArrowLeft className="h-4 w-4" /> Zurück zum Lageplan
        </Link>
      ) : (
        <ZurueckLink fallback="/graeber" fallbackLabel="Zurück zu den Gräbern" />
      )}

      <div className="mb-6 mt-3 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Grab {grabState.abteilung}/{grabState.grabnummer}</h1>
        <Badge key={grabState.status} className={`${statusBadge[grabState.status] || ''} ${ankunft(`grabstatus-${grabState.id}`).className && 'ui-wechsel'}`}>{STATUS_LABEL[grabState.status] || grabState.status}</Badge>
        {grabState.friedhofName && <span className="text-sm text-gray-500">{grabState.friedhofName}</span>}
      </div>

      <div className="space-y-6">
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner className="h-4 w-4 text-brand-600" /> Lade Details…</div>
          ) : (
            <>
              {/* Grab */}
              <Card ref={ankunft(`grab-${grabState.id}`).ref} className={`p-6 ${ankunft(`grab-${grabState.id}`).className}`}>
                <SectionTitle icon={<Icon.MapPin className="h-4 w-4" />} action={editing?.kind !== 'grab' && <EditLink onClick={startGrab}>Bearbeiten</EditLink>}>Grab</SectionTitle>
                {editing?.kind === 'grab' ? (
                  <form onSubmit={saveGrab} className="ui-aufklappen space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Abteilung" required><Input required value={form.abteilung} onChange={e => setForm({ ...form, abteilung: e.target.value })} /></Field>
                      <Field label="Grabnummer" required><Input required value={form.grabnummer} onChange={e => setForm({ ...form, grabnummer: e.target.value })} /></Field>
                    </div>
                    <Field label="Status"><Select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>{GRAB_STATUS.map(s => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}</Select></Field>
                    <Field label="Notizen"><Textarea rows={2} value={form.notizen} onChange={e => setForm({ ...form, notizen: e.target.value })} /></Field>
                    <FormActions>
                      <Button type="button" variant="secondary" onClick={cancel}>Abbrechen</Button>
                      <Button type="submit" loading={saving}>Speichern</Button>
                    </FormActions>
                  </form>
                ) : (
                  <div className="divide-y divide-gray-100">
                    <Row label="Abteilung" value={grabState.abteilung} />
                    <Row label="Grabnummer" value={grabState.grabnummer} />
                    <Row label="Status" value={STATUS_LABEL[grabState.status] || grabState.status} />
                    <Row label="Friedhof" value={grabState.friedhofName} />
                    <Row label="Notizen" value={grabState.notizen} />
                  </div>
                )}
              </Card>

              {/* Kunde (direkte Zuordnung des Grabs) */}
              <Card ref={ankunft(`grabkunde-${grabState.id}`).ref} className={`p-6 ${ankunft(`grabkunde-${grabState.id}`).className}`}>
                <SectionTitle
                  icon={<UserIcon />}
                  action={editing?.kind === 'kunde' ? undefined : (
                    <div className="flex items-center gap-1">
                      {directKunde && <Link to={`/kunden/${directKunde.id}`} className="rounded-md px-2 py-0.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50">Zum Kunden</Link>}
                      <EditLink onClick={startKunde}>{directKunde ? 'Ändern' : 'Zuordnen'}</EditLink>
                    </div>
                  )}
                >
                  Kunde
                </SectionTitle>
                {editing?.kind === 'kunde' ? (
                  <form onSubmit={saveKunde} className="ui-aufklappen space-y-3">
                    <Field label="Zugeordneter Kunde" hint="Optional – auch ohne Vertrag direkt zuordenbar">
                      <KundeSelect value={form.kundeId || undefined} kunden={kunden} onChange={id => setForm({ ...form, kundeId: id ?? 0 })} placeholder="Kunde suchen…" />
                    </Field>
                    <div className="flex items-center justify-between gap-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => setShowNeuerKunde(true)} icon={<Icon.Plus className="h-4 w-4" />}>Kunde anlegen</Button>
                      <div className="flex gap-2">
                        <Button type="button" variant="secondary" onClick={cancel}>Abbrechen</Button>
                        <Button type="submit" loading={saving}>Speichern</Button>
                      </div>
                    </div>
                  </form>
                ) : directKunde ? (
                  <div className="divide-y divide-gray-100">
                    <Row label="Name" value={<Link to={`/kunden/${directKunde.id}`} className="text-brand-700 hover:underline">{`${directKunde.anrede ? directKunde.anrede + ' ' : ''}${directKunde.vorname} ${directKunde.nachname}`}</Link>} />
                    <Row label="Kundennummer" value={directKunde.kundennummer} />
                    <Row label="Adresse" value={kundeAdresse} />
                    <Row label="Telefon" value={directKunde.telefon} />
                    <Row label="Mobil" value={directKunde.mobil} />
                    <Row label="E-Mail" value={directKunde.email} />
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">
                    <p>Noch kein Kunde direkt zugeordnet. Den Besitzer eines Tieres legt dessen Vertrag fest.</p>
                    <button type="button" onClick={startKunde} className="-ml-2 mt-1 rounded-md px-2 py-0.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50">Kunde zuordnen</button>
                  </div>
                )}
              </Card>

              {/* Aktuelle Belegung (Tiere) */}
              <Card className="p-6">
                <SectionTitle
                  icon={<Icon.Paw className="h-4 w-4" />}
                  action={<Button size="sm" variant="secondary" onClick={() => startTier()} icon={<Icon.Plus className="h-4 w-4" />}>Tier anlegen</Button>}
                >
                  Aktuelle Belegung
                </SectionTitle>

                {editing?.kind === 'tier' && editing.tierId === undefined && (
                  <div className="ui-aufklappen mb-4 rounded-xl border border-brand-200 bg-brand-50/40 p-4"><TierFormular grabId={grabState.id} onSaved={t => { markiereNeu(`tier-${t.id}`); afterSave('Tier angelegt'); }} onCancel={cancel} /></div>
                )}

                {aktuelleTiere.length === 0 && !(editing?.kind === 'tier' && editing.tierId === undefined) ? (
                  <div className="text-sm text-gray-400">
                    <p>Noch kein Tier in diesem Grab.</p>
                    <button type="button" onClick={() => startTier()} className="-ml-2 mt-1 rounded-md px-2 py-0.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50">Tier anlegen</button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {aktuelleTiere.map(tier => { const a = ankunft(`tier-${tier.id}`); return (
                      <div key={tier.id} ref={a.ref} className={`rounded-xl border border-gray-200 p-4 ${a.className}`}>
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                            <Icon.Paw className="h-4 w-4 text-gray-400" />
                            {tier.name || 'Tier'}{tier.art ? <span className="font-normal text-gray-500">({tier.art})</span> : null}
                          </h4>
                          {!(editing?.kind === 'tier' && editing.tierId === tier.id) && (
                            <div className="flex items-center gap-1">
                              <EditLink onClick={() => startTier(tier)}>Bearbeiten</EditLink>
                              <button type="button" onClick={() => deleteTier(tier)} className="rounded-md px-2 py-0.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50">Löschen</button>
                            </div>
                          )}
                        </div>
                        {editing?.kind === 'tier' && editing.tierId === tier.id ? (
                          <div className="ui-aufklappen"><TierFormular grabId={grabState.id} tier={tier} onSaved={t => { markiereNeu(`tier-${t.id}`); afterSave('Tier gespeichert'); }} onCancel={cancel} /></div>
                        ) : (
                          <>
                            <div className="divide-y divide-gray-100">
                              <Row label="Rasse" value={tier.rasse} />
                              <Row label="Geburtsdatum" value={tier.gebDatum && fmtDatum(tier.gebDatum)} />
                              <Row label="Sterbedatum" value={tier.sterbeDatum && fmtDatum(tier.sterbeDatum)} />
                              <Row label="Beisetzung" value={tier.beisetzungsdatum && fmtDatum(tier.beisetzungsdatum)} />
                              <Row label="Gewicht" value={tier.gewicht ? `${tier.gewicht} kg` : undefined} />
                              <Row label="Größe" value={tier.groesse} />
                            </div>
                            {vertragBlock(tier)}
                          </>
                        )}
                      </div>
                    ); })}
                  </div>
                )}
              </Card>

              {/* Historie */}
              {historieTiere.length > 0 && (
                <Card className="p-6">
                  <SectionTitle icon={<Icon.Clock className="h-4 w-4" />}>Historie</SectionTitle>
                  <div className="space-y-3">
                    {historieTiere.map(tier => {
                      const v = vertragByTier.get(tier.id);
                      const a = ankunft(`tier-${tier.id}`);
                      return (
                        <div key={tier.id} ref={a.ref} className={`rounded-xl border border-gray-200 bg-gray-50/60 p-4 ${a.className}`}>
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="flex items-center gap-2 font-medium text-gray-700">
                              <Icon.Paw className="h-4 w-4 text-gray-400" />
                              {tier.name || 'Tier'}{tier.art ? <span className="font-normal text-gray-500">({tier.art})</span> : null}
                            </h4>
                            <Badge key={v?.status} className={`bg-gray-200 text-gray-700 ${v && ankunft(`vertrag-${v.id}`).className ? 'ui-wechsel' : ''}`}>Beendet</Badge>
                          </div>
                          <div className="mt-1 divide-y divide-gray-100">
                            <Row label="Zeitraum" value={v ? `${fmtDatum(v.unterschriftsdatum)} – ${fmtDatum(v.endeDatum)}` : undefined} />
                            <Row label="Kunde" value={v?.kundeId ? <Link to={`/kunden/${v.kundeId}`} className="text-brand-700 hover:underline">{v.kundeName}</Link> : v?.kundeName} />
                            <Row label="Vertrag" value={v ? <Link to={`/vertraege/${v.id}`} className="text-brand-700 hover:underline">{v.vertragsnummer}</Link> : undefined} />
                            <Row label="Beisetzung" value={tier.beisetzungsdatum && fmtDatum(tier.beisetzungsdatum)} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Kartenausschnitt */}
        <div>
          <SectionTitle icon={<Icon.MapPin className="h-4 w-4" />}>Lage</SectionTitle>
          <GrabMiniMap grab={grabState} alle={alleGraeber} />
          <p className="mt-2 text-xs text-gray-400">Grünes Grab ausgewählt, umliegende Gräber blass.</p>
        </div>

        {/* Dokumente */}
        <DokumentSektion entityType="GRAB" entityId={grabState.id} />

        {/* Kommentare */}
        <KommentarSektion entityType="GRAB" entityId={grabState.id} />
      </div>

      {showNeuerKunde && (
        <KundeFormModal
          preselectGrabIds={[grabState.id]}
          onClose={() => setShowNeuerKunde(false)}
          onSaved={async () => { markiereNeu(`grabkunde-${grabState.id}`); setShowNeuerKunde(false); cancel(); await refresh(); }}
        />
      )}
    </div>
  );
};

const UserIcon: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

export default GrabDetail;
